import { task, timeout } from 'ember-concurrency';
import config from 'volta/config/environment';

import { isArray } from '@ember/array';
import Service, { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';

import { defaultErrorHandler } from '../utils/error-utils';

import type SessionService from 'ember-simple-auth/services/session';
import type WebSocketService from 'ember-websockets/services/websockets';
import type WebsocketProxy from 'ember-websockets/helpers/websocket-proxy';
import type Owner from "@ember/owner";

const MAX_RETRIES = 3;

export interface IEventMsg {
  channel: string;
  event: string;
  payload: object;
  user: { id: string; login: string };
}

export type IHandlerCallback = (payload: any, event: string, user: IEventMsg['user']) => void;

export type THandlerData = {
  context: unknown;
  handler: IHandlerCallback;
};

export default class EventStreamService extends Service {
  // Services
  // ~~~~~

  @service websockets!: WebSocketService;
  @service session!: SessionService;

  // Properties
  // ~~~~~

  /**
   * The WebSocket object reference
   * @type {WebSocket}
   */
  socket?: WebsocketProxy;

  /**
   * The registered message handlers
   * @type {Object}
   */
  handlers: Record<string, THandlerData[]> = {};

  /**
   * Is the connection online ?
   * @type {Boolean}
   */
  online: boolean = false;

  /**
   * Number of ms before retrying to reconnect after the connection was closed
   * @type {Number}
   */
  retryTimeout: number = 5000;

  retries: number = MAX_RETRIES;

  // Computed Properties
  // ~~~~~

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: Owner) {
    super(owner);
    this.initSocket();
  }

  // Helper Function
  // ~~~~~

  initSocket() {
    if (this.session.isAuthenticated && !this.socket) {
      console.debug('event-stream:initSocket', 'authenticated');
      const token = this.session.data.authenticated['token'];
      const host = `${config.api.wsprotocol}://${config.api.domain}:${config.api.wsport}/${config.api.namespace}/stream?token=${token}`;
      const socket = this.websockets.socketFor(host);
      socket.on('open', this.open, this);
      socket.on('message', this.handleMessage, this);
      socket.on('close', this.reconnect, this);
      this.socket = socket;
    } else {
      console.debug('event-stream:initSocket', 'not-authenticated');
    }

    return this.socket;
  }

  /**
   * On connection open event handler
   * @param  {Object} event The WebSocket event received
   */
  open(event: Event) {
    console.debug('event-stream:open', event);
    this.online = true;
    this.retries = MAX_RETRIES;
  }

  /**
   * Websocket message handler. Tries to find messages handlers and call them
   * with the payload in parameter
   * @param {Object} msg The websocket message
   */
  handleMessage(msg: MessageEvent) {
    const eventMsg = JSON.parse(msg.data);
    const eventKey = this.buildEventKey(eventMsg.channel, eventMsg.event);
    const handlers = this.handlers[eventKey];
    console.debug('event-stream:message', eventMsg);

    if (handlers !== undefined && handlers.length) {
      handlers.forEach((data) => {
        data.handler.call(
          data.context,
          { ...(eventMsg.payload ?? {}) },
          eventMsg.event,
          eventMsg.user
        );
      });
    }
  }

  /**
   * Registers a websocket message handler
   *
   * @param {string} channel The event channel (eq. "skus")
   * @param {string|string[]} event   The event name
   * @param {Function} handler The event handler function
   * @param {Object} context The event handler context (A controller or component...)
   */
  addHandler(
    channel: string,
    event: string | string[],
    handler: THandlerData['handler'],
    context: THandlerData['context']
  ) {
    this.initSocket();
    const events = isArray(event) ? (event as string[]) : [event];
    const keys = events.map((e) => this.buildEventKey(channel, e));
    const handlerData = { context: context, handler: handler };
    console.debug('event-stream:addHandler', `${keys.join(',')}`);

    keys.forEach((key) => {
      const existingHandlers = this.handlers[key];
      if (existingHandlers !== undefined) {
        existingHandlers.push(handlerData);
        this.handlers[key] = existingHandlers;
      } else {
        this.handlers[key] = [handlerData];
      }
    });
  }

  removeHandler(
    channel: string,
    event: string | string[],
    handler: THandlerData['handler'],
    context: THandlerData['context']
  ) {
    const events = isArray(event) ? (event as string[]) : [event];
    const keys = events.map((e) => this.buildEventKey(channel, e));
    const handlerData = { context: context, handler: handler };
    console.debug('event-stream:removeHandler', `${keys.join(',')}`);

    keys.forEach((key) => {
      const existingHandlers = this.handlers[key];
      if (existingHandlers) {
        this.handlers[key] = existingHandlers.filter((e) => e !== handlerData);
      }
    });
  }

  removeHandlers(context: unknown) {
    console.debug('event-stream:removeHandlers', `${context}`);
    const existingHandlers = this.handlers;
    if (isPresent(existingHandlers)) {
      for (const key in existingHandlers) {
        if (Object.prototype.hasOwnProperty.call(existingHandlers, key)) {
          const handlers = existingHandlers[key];
          if (handlers) {
            existingHandlers[key] = handlers.filter((h) => {
              return h['context'] !== context;
            });
          }
        }
      }
    }
  }

  /**
   * Build an event key from the channel and event name for caching handlers
   *
   * @param {String} channel The event channel
   * @param {String} event The event name
   */
  buildEventKey(channel: string, event: string) {
    return `${channel}_${event}`;
  }

  /**
   * When the socket connection is closed, tries to reconnect every
   *
   * @param {Object} closeEvent The close socket event
   */
  reconnect(closeEvent: Event) {
    console.debug('event-stream:reconnect', closeEvent);
    this.online = false;

    if (closeEvent instanceof CloseEvent && closeEvent.code === 1003) {
      // 1003: unsupported data
      defaultErrorHandler('1003: unsupported data');
      return;
    }

    this.reconnectTask.perform();
  }

  // Private Callbacks
  // ~~~~~

  reconnectTask = task(async () => {
    const { retries, socket } = this;

    if (retries <= MAX_RETRIES && retries > 0) {
      await timeout(this.retryTimeout);
      socket?.reconnect();
    } else {
      if (retries === 0) {
        this.session.invalidate();
      } else {
        this.retries = retries - 1;
      }
    }
  });
}

declare module '@ember/service' {
  interface Registry {
    'event-stream': EventStreamService;
  }
}
